import { useEffect } from 'react';
import { format } from 'date-fns';
import { Clock, Shield, ShieldOff, Star, Link2 } from 'lucide-react';
import clsx from 'clsx';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { Job, Staff } from '../data/mockData';

import { PHASE_DRAG_TYPE, type PhaseDragItem } from '../../lib/dndTypes';
import type { DayPosition } from '../../lib/boardUtils';
import { CARD_HEIGHT } from '../../lib/boardUtils';
export type { DayPosition };

// Phase 30-01: active-card left-border keys off the unified status (status +
// source + phaseStatus), not phaseStatus alone. Lifecycle takes precedence
// over commercial state so In Progress is visually distinct from scheduled.
// 80-02 collapse: the scheduled branch reads the single phaseStatus lifecycle.
// Derivation (preserves the pre-unification provisional-vs-committed split):
//   provisional → red; confirmed/in_progress/completed → committed yellow;
//   cancelled → falls through to neutral (the cancelled-card treatment is
//   handled by the tombstone branch upstream).
function activeCardBorderClass(
  status: Job['status'],
  source: Job['source'] | undefined,
  phaseStatus: Job['phaseStatus'] | undefined,
): string {
  if (status === 'in_progress') return 'border-l-[#83de9b]';
  if (status === 'scheduled') {
    if (source === 'hubspot_ticket') return 'border-l-violet-400';
    if (phaseStatus === 'provisional') return 'border-l-red-500';
    if (phaseStatus === 'confirmed' || phaseStatus === 'in_progress' || phaseStatus === 'completed')
      return 'border-l-yellow-400';
  }
  return 'border-l-neutral-300';
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '?';
  return ((parts[0][0] ?? '') + (parts[parts.length - 1][0] ?? '')).toUpperCase();
}

export function JobCard({
  job,
  staffMap,
  dayPosition = 'single',
  dayNumber = 1,
  cardDate,
  vehicleRegistrations,
  phaseCount = 1,
  dragMeta,
  onClick,
  onOpenSection,
}: {
  job: Job;
  staffMap: Map<string, Staff>;
  dayPosition?: DayPosition;
  dayNumber?: number;
  cardDate?: Date;
  // Phase 37-01 (Issue #51): vehicle registrations assigned to this (job, cardDate)
  // pair. Rendered as a 3×2 amber-pill grid in the middle of the card. Empty
  // array (or omitted) → no pills section. Tombstone cards skip pills entirely.
  vehicleRegistrations?: string[];
  // Phase 39-05 (AC-1/AC-2): number of non-cancelled phases on the parent job.
  // Drives the "Phase N of M" badge: hidden when M=1 (single-phase legacy UX
  // preserved verbatim); rendered when M>=2.
  phaseCount?: number;
  // #217: drag metadata for phase rescheduling. The board passes a populated
  // item only for a draggable start-card (permission + active phase + non-
  // terminal status); null/undefined ⇒ the card is not draggable (current
  // behaviour preserved verbatim for every existing caller/test).
  dragMeta?: PhaseDragItem | null;
  onClick: () => void;
  // #102: deep-link from a "+N" overflow chip — opens the JobDetailPanel with
  // the named section pre-expanded (vehicles or assigned staff). Optional so
  // existing call sites / tests that only pass onClick keep working.
  onOpenSection?: (section: 'vehicles' | 'staff') => void;
}) {
  // ── #217: phase drag source ──
  // Hooks must run unconditionally and ABOVE the tombstone early-returns below
  // (rules of hooks). `canDrag` gates begin-drag to a start/single card that the
  // board flagged draggable (dragMeta present); continuation cards (middle/end)
  // and non-draggable cards are inert. The connector is attached to the real
  // card <div> further down — never to a tombstone root (the board passes
  // dragMeta={null} for terminal phases anyway).
  const canDrag = !!dragMeta && (dayPosition === 'start' || dayPosition === 'single');
  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: PHASE_DRAG_TYPE,
      item: dragMeta ?? undefined,
      canDrag: () => canDrag,
      collect: (m) => ({ isDragging: m.isDragging() }),
    }),
    [dragMeta, canDrag],
  );
  // Suppress the native drag image — PhaseDragLayer renders the multi-day ghost.
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  // Filter to only the staff assigned on this specific date. Falls back to all
  // staff if cardDate is not provided (e.g. in tests that predate this feature).
  const dateKey = cardDate ? format(cardDate, 'yyyy-MM-dd') : null;
  const dayStaff = dateKey
    ? job.assignedStaff
        .filter((a) => a.date === dateKey)
        .map((a) => {
          const staff = staffMap.get(a.staffId);
          return {
            ...a,
            name: staff?.knownAs || staff?.name,
            initials: staff?.initials,
            role: staff?.role,
          };
        })
    : job.assignedStaff.map((a) => {
        const staff = staffMap.get(a.staffId);
        return {
          ...a,
          name: staff?.knownAs || staff?.name,
          initials: staff?.initials,
          role: staff?.role,
        };
      });

  // Sort: Reprotec staff alphabetically, then subcontractors alphabetically
  const sortedStaff = [...dayStaff].sort((a, b) => {
    const aSub = a.role === 'subcontractor' ? 1 : 0;
    const bSub = b.role === 'subcontractor' ? 1 : 0;
    if (aSub !== bSub) return aSub - bSub;
    return (a.name ?? '').localeCompare(b.name ?? '');
  });

  // Pin contracts manager badge first — inject from staffMap if not already in assignments
  if (job.contractsManagerId) {
    const mgrIdx = sortedStaff.findIndex((s) => s.staffId === job.contractsManagerId);
    if (mgrIdx > 0) {
      const [mgr] = sortedStaff.splice(mgrIdx, 1);
      sortedStaff.unshift(mgr);
    } else if (mgrIdx === -1) {
      // Manager not in assignedStaff — inject a synthetic badge entry from staffMap
      const mgrRecord = staffMap.get(job.contractsManagerId);
      if (mgrRecord) {
        sortedStaff.unshift({
          staffId: job.contractsManagerId,
          assignmentId: '',
          hours: 0,
          startTime: '',
          endTime: '',
          date: '',
          isTeamLeader: false,
          name: mgrRecord.knownAs || mgrRecord.name,
          initials: mgrRecord.initials,
          role: mgrRecord.role,
        });
      }
    }
  }

  // Extract Team Leader from the sorted list (renders as a TL shield in the TOP
  // row — #93/#94 relocation — not a regular avatar).
  const hasOperatives = sortedStaff.some((s) => s.role === 'operative');
  const tlIndex = sortedStaff.findIndex((s) => s.isTeamLeader === true && s.role === 'operative');
  const teamLeader = tlIndex !== -1 ? sortedStaff.splice(tlIndex, 1)[0] : null;

  // #93: the Contracts Manager (Star badge) moves to the TOP row alongside the
  // TL shield. Extract it out of sortedStaff so the bottom row renders worker
  // avatars only (operatives/subcontractors), with neither CM nor TL doubled up.
  const cmIndex = job.contractsManagerId
    ? sortedStaff.findIndex((s) => s.staffId === job.contractsManagerId)
    : -1;
  const contractsManager = cmIndex !== -1 ? sortedStaff.splice(cmIndex, 1)[0] : null;

  // Remaining staff = worker avatars for the bottom row (CM + TL already pulled).
  const workerStaff = sortedStaff;
  // #93/#102 (Tom 2026-06-09): the worker avatar area spans up to TWO rows
  // before a clickable "+N more" chip that deep-links into the panel's Assigned
  // Staff section. The count includes BOTH operatives and subcontractors (only
  // the CM Star + TL Shield are pulled to the top row); Tom set the threshold at
  // 10 assigned staff+subs (UAT 2026-06-10). The card height was bumped by one
  // avatar-row's worth (CARD_HEIGHT 128 → 152) to make room, and the avatar
  // container is clamped to a fixed two-row max-height with overflow-hidden so
  // the card can never grow past two rows even if the cap is exceeded.
  const WORKER_LIMIT = 10;
  const visibleWorkers = workerStaff.slice(0, WORKER_LIMIT);
  const hiddenWorkerCount = workerStaff.length - visibleWorkers.length;

  const dayScope = dateKey ? (job.dailyScopes?.[dateKey] ?? null) : null;

  // Phase 30-01: ticket-sourced jobs render the literal "TICKET" string in the
  // ION position (the `T-{source_id}` placeholder is operationally meaningless
  // to planners). Applies to both active card and tombstone variants.
  const ionDisplay = job.source === 'hubspot_ticket' ? 'TICKET' : (job.ion ?? '—');

  // #112: 1–2 char labels stay fixed circles (w-5); 3+ char labels (e.g.
  // subcontractor "MJF1"/"MJF2") become auto-width pills (px-1.5 min-w-5,
  // whitespace-nowrap) so the text never spills out of / clips the circle.
  // Same h-5 height keeps avatar rows aligned; colour semantics unchanged.
  const avatarClass = (role: string | undefined, label: string) =>
    clsx(
      'inline-flex items-center justify-center h-5 rounded-full text-[9px] font-bold shrink-0',
      label.length >= 3 ? 'px-1.5 min-w-5 whitespace-nowrap' : 'w-5',
      role === 'manager'
        ? 'bg-purple-100 text-purple-700'
        : role === 'subcontractor'
          ? 'bg-[#ccf5ff]/70 text-neutral-900'
          : 'bg-[#F26522]/20 text-neutral-900',
    );

  // Phase 41-03 (#66) UAT 2026-06-01: Tombstone branch precedence.
  //
  // The PARENT-job terminal tombstone (cancelled/completed/postponed) below
  // used to fire FIRST — but that incorrectly inherited the parent state on
  // every phase card after the cascade-up flipped the parent to 'completed'.
  // Tom UAT clarified the requirement: each phase must retain its OWN visual
  // state independently. So phase-level tombstones now win in multi-phase
  // context (phaseCount >= 2). Single-phase jobs (phaseCount === 1) still
  // fall through to the parent tombstone — a single-phase completed job
  // doesn't need "Phase 1 of 1" decoration; the cleaner "COMPLETED" wins.
  //
  // Postponed stays as a parent-only state (postpone is whole-job; per-phase
  // postpone is deferred per 41-03 Q6 future-proofing).

  const isMultiPhase = phaseCount >= 2;

  // Phase-level CANCELLED tombstone (multi-phase only; original Phase 39-05
  // AC-3 behaviour preserved — now also fires when parent has cascade-
  // completed, since the cancelled phase must keep its own state).
  if (isMultiPhase && job.phaseStatus === 'cancelled') {
    return (
      <div
        onClick={onClick}
        className="rounded-md bg-neutral-100 border border-neutral-200 px-2.5 py-1.5 space-y-0.5 opacity-75 overflow-hidden cursor-pointer hover:bg-neutral-200 transition-colors"
        style={{ height: CARD_HEIGHT }}
      >
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-semibold text-neutral-600 shrink-0 whitespace-nowrap">
            {ionDisplay}
          </span>
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide shrink-0 bg-red-100 text-red-700">
            Phase Cancelled
          </span>
        </div>
        {job.phaseNumber != null && (
          <span className="text-[10px] font-semibold text-neutral-500 leading-tight">
            Phase {job.phaseNumber} of {phaseCount}
          </span>
        )}
        <p className="text-[11px] text-neutral-500 truncate leading-tight line-through">
          {job.displayName || job.dealName}
        </p>
      </div>
    );
  }

  // Phase-level COMPLETED tombstone (multi-phase only; UAT extension).
  if (isMultiPhase && job.phaseStatus === 'completed') {
    return (
      <div
        onClick={onClick}
        className="rounded-md bg-neutral-100 border border-neutral-200 px-2.5 py-1.5 space-y-0.5 opacity-75 overflow-hidden cursor-pointer hover:bg-neutral-200 transition-colors"
        style={{ height: CARD_HEIGHT }}
      >
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-semibold text-neutral-600 shrink-0 whitespace-nowrap">
            {ionDisplay}
          </span>
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide shrink-0 bg-emerald-100 text-emerald-800">
            Phase Completed
          </span>
        </div>
        {job.phaseNumber != null && (
          <span className="text-[10px] font-semibold text-neutral-500 leading-tight">
            Phase {job.phaseNumber} of {phaseCount}
          </span>
        )}
        <p className="text-[11px] text-neutral-500 truncate leading-tight">
          {job.displayName || job.dealName}
        </p>
      </div>
    );
  }

  // PARENT-JOB terminal tombstone (cancelled/completed/postponed) — single-
  // phase context, or postponed (whole-job state). Still clickable so the
  // planner can open the (read-only) JobDetailPanel.
  if (job.status === 'cancelled' || job.status === 'completed' || job.status === 'postponed') {
    const isCompleted = job.status === 'completed';
    const isPostponed = job.status === 'postponed';
    const badgeLabel = isCompleted ? 'Completed' : isPostponed ? 'Postponed' : 'Cancelled';
    const badgeClasses = isCompleted
      ? 'bg-emerald-100 text-emerald-800'
      : isPostponed
        ? 'bg-[#F26522] text-white'
        : 'bg-red-100 text-red-700';
    return (
      <div
        onClick={onClick}
        className="rounded-md bg-neutral-100 border border-neutral-200 px-2.5 py-1.5 space-y-0.5 opacity-75 overflow-hidden cursor-pointer hover:bg-neutral-200 transition-colors"
        style={{ height: CARD_HEIGHT }}
      >
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-semibold text-neutral-600 shrink-0 whitespace-nowrap">
            {ionDisplay}
          </span>
          <span
            className={clsx(
              'inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide shrink-0',
              badgeClasses,
            )}
          >
            {badgeLabel}
          </span>
        </div>
        <p className="text-[11px] text-neutral-500 truncate leading-tight">{job.displayName}</p>
        {dayScope && (
          <p className="text-[10px] text-neutral-400 truncate leading-tight">{dayScope}</p>
        )}
      </div>
    );
  }

  // Phase 39-05 (AC-3): cancelled-phase tombstone. Runs AFTER the parent-status
  // branch above so a fully-cancelled job lands as plain "Cancelled" — this
  // branch only fires when the PARENT is still 'scheduled' and a single phase
  // is cancelled. Distinct visual (Phase N cancelled badge + strike-through
  // title) gives the planner a clear audit signal that one phase of an
  // otherwise-live multi-phase job has been pulled. No vehicle pills / staff
  // avatars — purely audit signal. Click still opens JobDetailPanel; 39-06
  // will decide read-only treatment.
  // (Phase 39-05 cancelled-phase tombstone + 41-03 UAT completed-phase
  // tombstone moved above, BEFORE the parent-terminal branch — see
  // "Tombstone branch precedence" note further up.)

  const isContinuation = dayPosition === 'middle' || dayPosition === 'end';
  const dayBadge = job.durationDays <= 1 ? '1d' : `${dayNumber} of ${job.durationDays}`;

  return (
    <TooltipProvider>
      <div
        ref={(node) => {
          // react-dnd v16 + React 19: `ref={drag}` does not typecheck (the
          // connector returns ReactElement|null). A void-returning callback ref
          // attaches the drag source to the real card DOM node.
          drag(node);
        }}
        data-draggable={canDrag || undefined}
        onClick={onClick}
        className={clsx(
          'relative flex flex-col rounded-lg border-l-[3px] shadow-sm cursor-pointer ring-1 ring-neutral-100 hover:ring-[#F26522]/30 transition-all group p-2 hover:shadow-md overflow-hidden',
          isContinuation ? 'bg-neutral-50/80 hover:bg-white' : 'bg-white',
          activeCardBorderClass(job.status, job.source, job.phaseStatus),
          canDrag && 'cursor-grab',
          isDragging && 'opacity-50 cursor-grabbing',
        )}
        style={{ height: CARD_HEIGHT }}
      >
        {/* ── Header: ION + CM star + TL shield + day badge ──
            #93/#94: the Building2 + MapPin + FileText status trio is removed
            from JOB cards. Instead the top row now carries the leadership
            badges — the Contracts Manager (Star + initials) and the Team
            Leader shield (3-state, #94) — sit next to the ION, with the
            clock + "N of M" day badge pushed to the right.

            Ticket cards (source=hubspot_ticket) keep their single Link2
            linked-deal icon unchanged — they have no CM/TL leadership concept
            on the card surface. */}
        <div className="flex justify-between items-center mb-1 gap-1.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-[11px] text-neutral-500 tracking-wide font-mono shrink-0 whitespace-nowrap">
              {ionDisplay}
            </span>
            {job.source === 'hubspot_ticket' ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link2
                    className={clsx(
                      'w-3 h-3 shrink-0',
                      job.linkedDealId ? 'text-emerald-500' : 'text-neutral-300',
                    )}
                    aria-label={job.linkedDealId ? 'linked to deal' : 'no linked deal'}
                  />
                </TooltipTrigger>
                <TooltipContent>
                  {job.linkedDealId ? 'Linked to deal' : 'No linked deal'}
                </TooltipContent>
              </Tooltip>
            ) : (
              <>
                {/* Contracts Manager — Star + initials (relocated from the
                    bottom avatar row in #93). */}
                {contractsManager && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center justify-center w-6 h-6 shrink-0 relative">
                        <Star className="w-6 h-6 text-[#F26522]/20 fill-[#F26522]/20" />
                        <span className="absolute inset-0 flex items-center justify-center pt-[1px] text-[9px] font-bold text-black leading-none">
                          {contractsManager.initials ||
                            (contractsManager.name ? getInitials(contractsManager.name) : '?')}
                        </span>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      Contracts Manager: {contractsManager.name ?? 'Unknown'}
                    </TooltipContent>
                  </Tooltip>
                )}
                {/* Team Leader shield — exactly ONE of three states (#94):
                    assigned (solid + initials), gap (grey outline), or
                    N/A (ShieldOff, subcontracted — no operatives). */}
                {teamLeader ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center justify-center w-6 h-6 shrink-0 relative">
                        <Shield className="w-6 h-6 text-[#F26522]/20 fill-[#F26522]/20" />
                        <span className="absolute inset-0 flex items-center justify-center pt-[1px] text-[9px] font-bold text-black leading-none">
                          {teamLeader.initials ||
                            (teamLeader.name ? getInitials(teamLeader.name) : '?')}
                        </span>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      Team Lead: {teamLeader.name ?? 'Unknown'}
                      {teamLeader.startTime
                        ? ` ${teamLeader.startTime.slice(0, 5)}–${teamLeader.endTime.slice(0, 5)}`
                        : ''}
                    </TooltipContent>
                  </Tooltip>
                ) : hasOperatives ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center justify-center w-6 h-6 shrink-0">
                        <Shield className="w-5 h-5 text-neutral-300" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>Team Leader not assigned</TooltipContent>
                  </Tooltip>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center justify-center w-6 h-6 shrink-0">
                        <ShieldOff
                          className="w-5 h-5 text-neutral-300"
                          aria-label="team leader n/a"
                        />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>Team Leader N/A (subcontracted)</TooltipContent>
                  </Tooltip>
                )}
              </>
            )}
          </div>
          <span className="flex items-center gap-0.5 text-[10px] text-neutral-400 shrink-0">
            <Clock className="w-2.5 h-2.5" />
            {dayBadge}
          </span>
        </div>

        {/* ── Phase 39-05 (AC-1): phase badge ──
            Rendered only when the parent job has 2+ active (non-cancelled)
            phases. Single-phase legacy jobs (phaseCount = 1, the default)
            hide the badge so today's UX is preserved verbatim. Q9 stable
            numbering means `phase_number` can be e.g. 5 with phaseCount=3
            (gaps from cancelled phases) — display as-is. */}
        {phaseCount >= 2 && job.phaseNumber != null && (
          <span className="text-[10px] font-semibold text-neutral-500 leading-tight mb-0.5">
            Phase {job.phaseNumber} of {phaseCount}
          </span>
        )}

        {/* ── Title (always, displayName) ── */}
        <h3 className="text-[12px] text-neutral-900 group-hover:text-[#F26522] transition-colors truncate mb-0.5 leading-tight">
          {job.displayName || job.dealName}
        </h3>

        {/* ── Per-day scope preview ── */}
        {dayScope && (
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="text-[10px] text-neutral-500 truncate leading-tight mb-0.5">
                {dayScope}
              </p>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs whitespace-pre-wrap">{dayScope}</TooltipContent>
          </Tooltip>
        )}

        {/* ── #93 (rev, Tom 2026-06-09): vehicle registration plates ──
            SINGLE line, low-priority: at most 2 FULL plates then a "+N" chip
            (e.g. "NA54 LXX  ND72 TXX  +2"). Staff-size font (text-[9px]) so
            they don't visually outrank the job name or staff; whitespace-nowrap
            shows the full reg (never truncated). One line only → the job NAME
            above always stays fully visible (no 2-line wrap pushing it out). */}
        {vehicleRegistrations && vehicleRegistrations.length > 0 && (
          <div className="flex items-center gap-1 mt-1 mb-1 overflow-hidden">
            {vehicleRegistrations.slice(0, 2).map((reg) => (
              <span
                key={reg}
                className="text-[9px] font-semibold tracking-wide px-1 py-0.5 rounded border bg-amber-50 border-amber-200 text-amber-900 whitespace-nowrap shrink-0"
              >
                {reg}
              </span>
            ))}
            {vehicleRegistrations.length > 2 && (
              <button
                type="button"
                onClick={(e) => {
                  // #102: open the panel with the Vehicles section expanded.
                  // Stop propagation so the card's own onClick (which opens the
                  // panel with no section expanded) doesn't also fire.
                  e.stopPropagation();
                  onOpenSection?.('vehicles');
                }}
                className="text-[9px] font-semibold text-amber-700 shrink-0 whitespace-nowrap rounded hover:bg-amber-100 hover:text-amber-900 transition-colors px-0.5 cursor-pointer"
                aria-label={`Show all ${vehicleRegistrations.length} vehicles`}
              >
                +{vehicleRegistrations.length - 2}
              </button>
            )}
          </div>
        )}

        {/* ── #93: worker avatar area — operatives/subcontractors only ──
            The Contracts Manager and Team Leader now live in the TOP row, so
            this area is purely the remaining workers. Up to 12 avatars (two
            rows of ~6) then a neutral, clickable "+N more" chip (#102:
            deep-links to the panel's Assigned Staff section). Colour semantics
            preserved via avatarClass(role).

            #93/#102 (2026-06-09): grows to TWO rows max. `max-h-[42px]`
            (two h-5 ≈20px rows + one gap-0.5 ≈2px) + `overflow-hidden` clamps
            the area so the card never exceeds CARD_HEIGHT even if the 12-cap is
            somehow exceeded. `mt-auto` keeps it anchored at the BOTTOM, and
            `content-end` makes wrapped rows stack UPWARD from the bottom (row 1
            sits above row 2) rather than top-down. */}
        <div className="flex flex-wrap content-end items-center gap-0.5 mt-auto pt-1.5 max-h-[42px] overflow-hidden">
          {visibleWorkers.length > 0 ? (
            <>
              {visibleWorkers.map((s, idx) => {
                const label = s.initials || (s.name ? getInitials(s.name) : '?');
                return (
                  <Tooltip key={s.staffId || idx}>
                    <TooltipTrigger asChild>
                      <span className={avatarClass(s.role, label)}>{label}</span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {s.name ?? 'Unknown'}
                      {s.startTime ? ` · ${s.startTime.slice(0, 5)}–${s.endTime.slice(0, 5)}` : ''}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
              {hiddenWorkerCount > 0 && (
                <button
                  type="button"
                  onClick={(e) => {
                    // #102: open the panel with the Assigned Staff section
                    // expanded. Stop propagation so the card onClick doesn't
                    // also fire (which would open with no section expanded).
                    e.stopPropagation();
                    onOpenSection?.('staff');
                  }}
                  className="inline-flex items-center justify-center px-1.5 h-5 rounded-full bg-neutral-100 text-neutral-500 text-[9px] font-semibold shrink-0 hover:bg-neutral-200 hover:text-neutral-700 transition-colors cursor-pointer"
                  aria-label={`Show all ${workerStaff.length} assigned staff`}
                >
                  +{hiddenWorkerCount} more
                </button>
              )}
            </>
          ) : (
            <span className="text-[10px] text-neutral-400 italic">
              {/* #115: a Team Leader is still an assigned staff member, so don't
                  call the card "Unassigned" when only the TL is on it. */}
              {teamLeader ? 'Team Lead assigned only' : 'Unassigned'}
            </span>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}
